import { useState, useEffect } from 'react'
import { ArrowLeft, ArrowRight, Lightbulb } from 'lucide-react'
import { SprintDetails } from '../pages/SprintReport'
import { trelloService } from '../lib/trello'

interface SprintConfigurationProps {
  boardId: string
  onConfigure: (details: SprintDetails) => void
  onBack: () => void
}

interface Label {
  id: string
  name: string
  color: string
}

export default function SprintConfiguration({ boardId, onConfigure, onBack }: SprintConfigurationProps) {
  const [labels, setLabels] = useState<Label[]>([])
  const [loading, setLoading] = useState(true)
  const [sprintLabel, setSprintLabel] = useState('')
  const [sprintDescription, setSprintDescription] = useState('')
  const [sprintDuration, setSprintDuration] = useState('2 weeks')
  const [sprintGoal, setSprintGoal] = useState('')
  const [customLabel, setCustomLabel] = useState('')
  const [showCustomLabel, setShowCustomLabel] = useState(false)

  useEffect(() => {
    fetchBoardLabels()
  }, [boardId])

  const fetchBoardLabels = async () => {
    try {
      setLoading(true)
      const boardLabels = await trelloService.getBoardLabels(boardId)
      setLabels(boardLabels)
    } catch (error) {
      console.error('Error fetching board labels:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLabelSelect = (labelName: string) => {
    setSprintLabel(labelName)
    setShowCustomLabel(false)
  }

  const handleCustomLabel = () => {
    setShowCustomLabel(true)
    setSprintLabel('')
  }

  const handleCustomLabelChange = (value: string) => {
    setCustomLabel(value)
    setSprintLabel(value)
  }

  const handleSubmit = () => {
    if (!sprintLabel.trim()) {
      alert('Please enter a sprint label')
      return
    }

    if (!sprintDescription.trim()) {
      alert('Please provide sprint details')
      return
    }

    const details: SprintDetails = {
      label: sprintLabel,
      description: sprintDescription,
      duration: sprintDuration,
      goal: sprintGoal
    }

    onConfigure(details)
  }

  const isFormValid = sprintLabel.trim() && sprintDescription.trim()

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Loading Board Labels</h2>
          <p className="text-gray-600">Fetching available labels...</p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Configure Sprint Analysis</h2>
        <p className="text-gray-600">
          Specify the sprint label and provide details to help generate better insights
        </p>
      </div>

      <div className="space-y-6">
        {/* Sprint Label Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sprint Label *
          </label>
          
          {!showCustomLabel ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {labels.slice(0, 6).map((label) => (
                  <button
                    key={label.id}
                    onClick={() => handleLabelSelect(label.name)}
                    className={`p-3 border rounded-lg text-left transition-all ${
                      sprintLabel === label.name
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: label.color }}
                      ></div>
                      <span className="text-sm font-medium">{label.name}</span>
                    </div>
                  </button>
                ))}
              </div>
              
              <button
                onClick={handleCustomLabel}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                + Use custom label
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                type="text"
                value={customLabel}
                onChange={(e) => handleCustomLabelChange(e.target.value)}
                placeholder="Enter sprint label (e.g., Sprint-23, Q1-Sprint-1)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button
                onClick={() => setShowCustomLabel(false)}
                className="text-gray-600 hover:text-gray-700 text-sm"
              >
                ← Back to existing labels
              </button>
            </div>
          )}
        </div>

        {/* Sprint Details */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sprint Details *
          </label>
          <textarea
            value={sprintDescription}
            onChange={(e) => setSprintDescription(e.target.value)}
            placeholder="Describe what this sprint was about (e.g., User authentication feature development with mobile app integration and API improvements)"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Sprint Duration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sprint Duration
          </label>
          <select
            value={sprintDuration}
            onChange={(e) => setSprintDuration(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="1 week">1 week</option>
            <option value="2 weeks">2 weeks</option>
            <option value="3 weeks">3 weeks</option>
            <option value="4 weeks">4 weeks</option>
            <option value="6 weeks">6 weeks</option>
            <option value="8 weeks">8 weeks</option>
          </select>
        </div>

        {/* Sprint Goal */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sprint Goal
          </label>
          <input
            type="text"
            value={sprintGoal}
            onChange={(e) => setSprintGoal(e.target.value)}
            placeholder="What was the main goal of this sprint?"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Helpful Tip */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-900 mb-1">💡 Tip</h4>
              <p className="text-sm text-blue-700">
                The more details you provide about your sprint, the better insights we can generate. 
                Include information about the main features, challenges, and team focus areas.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
        
        <button
          onClick={handleSubmit}
          disabled={!isFormValid}
          className={`flex items-center space-x-2 px-6 py-2 rounded-md font-medium ${
            isFormValid
              ? 'bg-primary-500 text-white hover:bg-primary-600'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <span>Start Analysis</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
} 